import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]" }
const _hoisted_2 = {
  key: 0,
  class: "bg-red-50 text-red-600 p-4 rounded-xl border border-red-200"
}
const _hoisted_3 = {
  key: 1,
  class: "bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200/50 dark:border-slate-700/50 overflow-hidden"
}
const _hoisted_4 = { class: "hidden lg:block overflow-x-auto" }
const _hoisted_5 = { class: "min-w-full divide-y divide-slate-200/50 dark:divide-slate-700/50" }
const _hoisted_6 = { class: "divide-y divide-slate-200/50 dark:divide-slate-700/50" }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "px-6 py-4" }
const _hoisted_9 = { class: "flex items-center gap-3 justify-start" }
const _hoisted_10 = ["src", "alt", "onError"]
const _hoisted_11 = { class: "min-w-0 text-left" }
const _hoisted_12 = { class: "text-sm font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_13 = { class: "text-sm text-slate-500 dark:text-slate-400" }
const _hoisted_14 = { class: "px-6 py-4 text-center" }
const _hoisted_15 = { class: "px-6 py-4 text-center" }
const _hoisted_16 = { class: "px-6 py-4" }
const _hoisted_17 = { class: "flex gap-2 justify-center" }
const _hoisted_18 = ["onClick"]
const _hoisted_19 = ["onClick"]
const _hoisted_20 = ["onClick"]
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "lg:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50" }
const _hoisted_23 = {
  key: 0,
  class: "p-8 flex justify-center"
}
const _hoisted_24 = {
  key: 1,
  class: "px-6 py-12 text-center text-slate-500"
}
const _hoisted_25 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_26 = ["src", "alt", "onError"]
const _hoisted_27 = { class: "min-w-0 flex-1" }
const _hoisted_28 = { class: "text-sm font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_29 = { class: "text-xs text-slate-500 dark:text-slate-400 truncate" }
const _hoisted_30 = { class: "text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" }
const _hoisted_31 = { class: "text-xs text-slate-500 dark:text-slate-400 mb-2" }
const _hoisted_32 = { class: "flex gap-2" }
const _hoisted_33 = ["onClick"]
const _hoisted_34 = ["onClick"]
const _hoisted_35 = ["onClick"]
const _hoisted_36 = ["onClick"]
const _hoisted_37 = {
  key: 2,
  class: "card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
}
const _hoisted_38 = { class: "p-4" }
const _hoisted_39 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_40 = { class: "font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_41 = ["disabled"]

import { ref, computed, onMounted, watch } from 'vue'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import { Eye, Trash, Download as DownloadIcon, Printer, Loader2, X } from 'lucide-vue-next'
import { useToast } from 'vue-toastification'
import TableControls from '@/components/layout/TableControls.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import TableLoader from '@/components/ui/TableLoader.vue'
import Modal from '@/components/layout/Modal.vue'
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal.vue'
import api from '@/services/api'

const toastTimeout = 5000


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminEmployeeDocumentsView',
  setup(__props) {

const toast = useToast()
const documents = ref<EmployeeDocument[]>([])
const loading = ref(false)
const error = ref('')

const searchQuery = ref('')
const statusFilter = ref('')
const perPage = ref(10)
const currentPage = ref(1)

const avatarLoadFailed = ref<Set<number>>(new Set())

const profileImageUrl = (doc: EmployeeDocument) => {
  const raw = String(doc.user?.profile_picture || '').trim()
  if (!raw || raw.toLowerCase() === 'null') return ''
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:image/') || raw.startsWith('blob:')) return raw
  const rawBase = String(api.defaults.baseURL || '').replace(/\/$/, '')
  const baseNoApi = rawBase.replace(/\/api$/i, '')
  if (raw.startsWith('/storage/')) return `${baseNoApi}${raw}`
  if (raw.startsWith('storage/')) return `${baseNoApi}/${raw}`
  return `${baseNoApi}/storage/${raw.replace(/^\//, '')}`
}

const shouldShowProfileImage = (doc: EmployeeDocument) => {
  const url = profileImageUrl(doc)
  return Boolean(url) && !avatarLoadFailed.value.has(doc.id)
}

const onProfileImageError = (doc: EmployeeDocument) => {
  avatarLoadFailed.value.add(doc.id)
}

const employeeName = (doc: EmployeeDocument) => {
  const f = doc.user?.details?.first_name || ''
  const l = doc.user?.details?.last_name || ''
  if (f && l) return `${f} ${l}`
  return doc.user?.email || 'Unknown'
}

const employeeInitials = (doc: EmployeeDocument) => {
  const f = doc.user?.details?.first_name || ''
  const l = doc.user?.details?.last_name || ''
  if (f && l) return (f[0] + l[0]).toUpperCase()
  const email = doc.user?.email || ''
  if (email) return email.substring(0, 2).toUpperCase()
  return 'EM'
}

const avatarBgClass = (doc: EmployeeDocument) => {
  const gradients = [
    'from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover',
    'from-success to-success-hover dark:from-success-dark dark:to-success-dark-hover',
    'from-info to-info-hover dark:from-info-dark dark:to-info-dark-hover',
    'from-warning to-warning-hover dark:from-warning-dark dark:to-warning-dark-hover',
    'from-danger to-danger-hover dark:from-danger-dark dark:to-danger-dark-hover',
  ]
  return gradients[doc.user_id % gradients.length]
}

const searchFiltered = computed(() => {
  if (!searchQuery.value.trim()) return documents.value
  const q = searchQuery.value.toLowerCase()
  return documents.value.filter(d => {
    const name = ((d.user?.details?.first_name || '') + ' ' + (d.user?.details?.last_name || '')).toLowerCase()
    const title = (d.title || '').toLowerCase()
    return name.includes(q) || title.includes(q)
  })
})

const filteredTotal = computed(() => searchFiltered.value.length)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))

const paginatedDocuments = computed(() => {
  const start = (currentPage.value - 1) * perPage.value
  return searchFiltered.value.slice(start, start + perPage.value)
})

watch([searchQuery, statusFilter, perPage], () => {
  currentPage.value = 1
})

const goToPage = (page: number) => {
  currentPage.value = page
}

const fetchDocuments = async () => {
  loading.value = true
  error.value = ''
  try {
    documents.value = await employeeDocumentService.fetchAll()
  } catch (err: any) {
    error.value = 'Failed to load employee documents.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDocuments()
})

const showViewModal = ref(false)
const selectedDocument = ref<EmployeeDocument | null>(null)
const showDeleteModal = ref(false)
const deleteLoading = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteTitle = ref('')

const viewDocument = (doc: EmployeeDocument) => {
  selectedDocument.value = doc
  showViewModal.value = true
}

const downloadDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(doc.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = doc.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch {
    toast.dismiss(loadingToast)
    toast.error('Failed to download document', { timeout: toastTimeout })
  }
}

const printDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for printing...', { timeout: false })
  try {
    await employeeDocumentService.printDocument(doc.id)
    toast.dismiss(loadingToast)
  } catch (err: any) {
    toast.dismiss(loadingToast)
    if (!err.message?.includes('opened in a new tab')) {
      toast.error(err.message || 'Failed to print document', { timeout: toastTimeout })
    }
  }
}

const handleDelete = (doc: EmployeeDocument) => {
  pendingDeleteId.value = doc.id
  pendingDeleteTitle.value = doc.title || 'this document'
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (pendingDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await employeeDocumentService.delete(pendingDeleteId.value)
    toast.success('Document deleted successfully', { timeout: toastTimeout })
    showDeleteModal.value = false
    pendingDeleteId.value = null
    fetchDocuments()
  } catch {
    toast.error('Failed to delete document', { timeout: toastTimeout })
  } finally {
    deleteLoading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[10] || (_cache[10] = _createElementVNode("div", { class: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" }, [
      _createElementVNode("div", null, [
        _createElementVNode("h1", { class: "text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:text-secondary" }, " Employee Documents "),
        _createElementVNode("p", { class: "text-sm text-slate-500 dark:text-slate-200 mt-2" }, " View important documents uploaded by employees. ")
      ])
    ], -1)),
    _createVNode(TableControls, {
      search: searchQuery.value,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
      status: statusFilter.value,
      "onUpdate:status": _cache[1] || (_cache[1] = ($event: any) => ((statusFilter).value = $event)),
      perPage: perPage.value,
      "onUpdate:perPage": _cache[2] || (_cache[2] = ($event: any) => ((perPage).value = $event)),
      placeholder: "Search by title or employee...",
      options: [{ value: '', label: 'All' }],
      filterLabel: "Filter",
      class: "md:sticky md:top-[80px]"
    }, null, 8, ["search", "status", "perPage"]),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(error.value), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("table", _hoisted_5, [
              _cache[7] || (_cache[7] = _createElementVNode("thead", { class: "bg-slate-50 dark:bg-slate-800/50" }, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", { class: "px-6 py-4 text-left text-xs font-bold text-slate-700 dark:text-slate-300 uppercase" }, "Employee"),
                  _createElementVNode("th", { class: "px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase" }, "Document Title"),
                  _createElementVNode("th", { class: "px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase" }, "Uploaded At"),
                  _createElementVNode("th", { class: "px-6 py-4 text-center text-xs font-bold text-slate-700 dark:text-slate-300 uppercase" }, "Action")
                ])
              ], -1)),
              _createElementVNode("tbody", _hoisted_6, [
                (loading.value)
                  ? (_openBlock(), _createBlock(TableLoader, {
                      key: 0,
                      colspan: 4,
                      title: "Fetching Documents"
                    }))
                  : (paginatedDocuments.value.length === 0)
                    ? (_openBlock(), _createElementBlock("tr", _hoisted_7, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("td", {
                          colspan: "4",
                          class: "px-6 py-12 text-center text-slate-500"
                        }, "No documents found.", -1)
                      ]))]))
                    : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedDocuments.value, (doc) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: doc.id,
                    class: "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }, [
                    _createElementVNode("td", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        (shouldShowProfileImage(doc))
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: profileImageUrl(doc),
                              alt: `${employeeName(doc)} photo`,
                              class: "h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0",
                              onError: ($event: any) => (onProfileImageError(doc))
                            }, null, 40, _hoisted_10))
                          : (_openBlock(), _createElementBlock("div", {
                              key: 1,
                              class: _normalizeClass(["h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0", avatarBgClass(doc)])
                            }, _toDisplayString(employeeInitials(doc)), 3)),
                        _createElementVNode("div", _hoisted_11, [
                          _createElementVNode("div", _hoisted_12, _toDisplayString(employeeName(doc)), 1),
                          _createElementVNode("div", _hoisted_13, _toDisplayString(doc.user?.email || ''), 1)
                        ])
                      ])
                    ]),
                    _createElementVNode("td", _hoisted_14, _toDisplayString(doc.title), 1),
                    _createElementVNode("td", _hoisted_15, _toDisplayString(`${new Date(doc.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })} [${new Date(doc.created_at).toLocaleTimeString('en-US')}]`), 1),
                    _createElementVNode("td", _hoisted_16, [
                      _createElementVNode("div", _hoisted_17, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (viewDocument(doc)),
                          class: "p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors",
                          title: "View"
                        }, [
                          _createVNode(_unref(Eye), { class: "w-5 h-5" })
                        ], 8, _hoisted_18),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (downloadDocument(doc)),
                          class: "p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors",
                          title: "Download"
                        }, [
                          _createVNode(_unref(DownloadIcon), { class: "w-5 h-5" })
                        ], 8, _hoisted_19),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (printDocument(doc)),
                          class: "p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors",
                          title: "Print"
                        }, [
                          _createVNode(_unref(Printer), { class: "w-5 h-5" })
                        ], 8, _hoisted_20),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (handleDelete(doc)),
                          class: "p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors",
                          title: "Delete"
                        }, [
                          _createVNode(_unref(Trash), { class: "w-5 h-5" })
                        ], 8, _hoisted_21)
                      ])
                    ])
                  ]))
                }), 128))
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_22, [
            (loading.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                  _createVNode(_unref(Loader2), { class: "w-8 h-8 animate-spin text-primary" })
                ]))
              : (paginatedDocuments.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_24, "No documents found."))
                : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(paginatedDocuments.value, (doc) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: doc.id,
                      class: "p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    }, [
                      _createElementVNode("div", _hoisted_25, [
                        (shouldShowProfileImage(doc))
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: profileImageUrl(doc),
                              alt: `${employeeName(doc)} photo`,
                              class: "h-10 w-10 rounded-full object-cover shadow-md flex-shrink-0",
                              onError: ($event: any) => (onProfileImageError(doc))
                            }, null, 40, _hoisted_26))
                          : (_openBlock(), _createElementBlock("div", {
                              key: 1,
                              class: _normalizeClass(["h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shadow-md bg-gradient-to-br flex-shrink-0", avatarBgClass(doc)])
                            }, _toDisplayString(employeeInitials(doc)), 3)),
                        _createElementVNode("div", _hoisted_27, [
                          _createElementVNode("div", _hoisted_28, _toDisplayString(employeeName(doc)), 1),
                          _createElementVNode("div", _hoisted_29, _toDisplayString(doc.user?.email || ''), 1)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_30, _toDisplayString(doc.title), 1),
                      _createElementVNode("div", _hoisted_31, _toDisplayString(new Date(doc.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })) + " [" + _toDisplayString(new Date(doc.created_at).toLocaleTimeString('en-US')) + "]", 1),
                      _createElementVNode("div", _hoisted_32, [
                        _createElementVNode("button", {
                          onClick: ($event: any) => (viewDocument(doc)),
                          class: "p-2 text-blue-600 hover:bg-primary/10 rounded-lg transition-colors",
                          title: "View"
                        }, [
                          _createVNode(_unref(Eye), { class: "w-5 h-5" })
                        ], 8, _hoisted_33),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (downloadDocument(doc)),
                          class: "p-2 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors",
                          title: "Download"
                        }, [
                          _createVNode(_unref(DownloadIcon), { class: "w-5 h-5" })
                        ], 8, _hoisted_34),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (printDocument(doc)),
                          class: "p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors",
                          title: "Print"
                        }, [
                          _createVNode(_unref(Printer), { class: "w-5 h-5" })
                        ], 8, _hoisted_35),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (handleDelete(doc)),
                          class: "p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors",
                          title: "Delete"
                        }, [
                          _createVNode(_unref(Trash), { class: "w-5 h-5" })
                        ], 8, _hoisted_36)
                      ])
                    ]))
                  }), 128))
          ])
        ])),
    (!loading.value && !error.value && filteredTotal.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
          _createElementVNode("div", _hoisted_38, [
            _createVNode(PaginationBar, {
              "current-page": currentPage.value,
              "total-pages": filteredTotalPages.value,
              total: filteredTotal.value,
              "per-page": perPage.value,
              "onUpdate:page": goToPage
            }, null, 8, ["current-page", "total-pages", "total", "per-page"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(Modal, {
      modelValue: showDeleteModal.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showDeleteModal).value = $event)),
      title: "Delete Document"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (showDeleteModal.value = false))
        }, "Cancel"),
        _createElementVNode("button", {
          type: "button",
          class: "ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed",
          disabled: deleteLoading.value,
          onClick: confirmDelete
        }, _toDisplayString(deleteLoading.value ? 'Deleting...' : 'Yes, delete permanently'), 9, _hoisted_41)
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_39, [
          _cache[8] || (_cache[8] = _createTextVNode(" Are you sure you want to permanently delete ", -1)),
          _createElementVNode("span", _hoisted_40, "\"" + _toDisplayString(pendingDeleteTitle.value) + "\"", 1),
          _cache[9] || (_cache[9] = _createTextVNode("? This action cannot be undone. ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(DocumentDetailsModal, {
      "is-open": showViewModal.value,
      document: selectedDocument.value,
      onClose: _cache[5] || (_cache[5] = ($event: any) => {showViewModal.value = false; selectedDocument.value = null})
    }, null, 8, ["is-open", "document"])
  ]))
}
}

})